import { useState } from "react";

const NAVY = "#0D1B2A";
const NAVY2 = "#1A2B3C";
const NAVY3 = "#243447";
const AMBER = "#F4A843";
const TEAL = "#4EC9B0";
const PURPLE = "#C586C0";
const GREEN = "#6A9955";
const STRING = "#CE9178";
const NUM = "#B5CEA8";
const MUTED = "#7A8FA6";
const TEXT = "#D4E0EE";
const TEXT2 = "#A8BCCF";

const sampleJson = {
  name: "John",
  course: "English",
  address: [
    { state: "Delhi", city: "New Delhi-0" },
    { state: "Delhi", city: "New Delhi-1" },
  ],
};

const CONCEPTS = [
  {
    title: "JSON object → Python dict",
    json: '{\n  "name": "John"\n}',
    python: 'data = {"name": "John"}\ndata["name"]              # "John"\ndata.get("name", "default")',
    note: "Curly braces {} in JSON always become a Python dict. Access values with data['key'] or the safer data.get('key', default).",
  },
  {
    title: "JSON array → Python list",
    json: '[\n  "New Delhi-0",\n  "New Delhi-1"\n]',
    python: 'cities = ["New Delhi-0", "New Delhi-1"]\ncities[0]      # "New Delhi-0"\nlen(cities)    # 2\nfor c in cities:\n    print(c)',
    note: "Square brackets [] in JSON become a Python list. Indexed from 0, supports for loops and list comprehensions.",
  },
  {
    title: "Nested structure",
    json: '{\n  "address": [\n    {"city": "New Delhi-0"},\n    {"city": "New Delhi-1"}\n  ]\n}',
    python: '# Access nested value\ndata["address"][0]["city"]   # "New Delhi-0"\n\n# All cities — list comprehension\ncities = [a["city"] for a in data["address"]]',
    note: "Combine dict and list access to drill into nested structures. data['address'] returns a list, then [0] picks the first dict.",
  },
  {
    title: "Type mapping table",
    json: '"string"\n123\n3.14\ntrue / false\nnull',
    python: 'str      → "John"\nint      → 123\nfloat    → 3.14\nbool     → True / False\nNoneType → None',
    note: "JSON true/false map to Python True/False (capital T/F). JSON null maps to Python None.",
  },
];

const METHODS = [
  {
    fn: "json.load(f)",
    sig: "file object → dict",
    use: "Reading a local .json file",
    code: 'import json\n\nwith open("cluster.json", "r", encoding="utf-8") as f:\n    data = json.load(f)\n\nprint(data["name"])  # John',
    tag: "FILE",
    tagColor: TEAL,
  },
  {
    fn: "json.loads(s)",
    sig: "string → dict",
    use: "Parsing API / HTTP responses",
    code: 'import json\n\nresponse_text = \'{"name": "John"}\'\ndata = json.loads(response_text)\n\nprint(data["name"])  # John',
    tag: "STRING",
    tagColor: PURPLE,
  },
  {
    fn: "json.dump(obj, f)",
    sig: "dict → file",
    use: "Writing a dict to a .json file",
    code: 'import json\n\ndata = {"name": "John"}\n\nwith open("out.json", "w") as f:\n    json.dump(data, f, indent=2)\n# Produces a nicely-indented JSON file',
    tag: "WRITE",
    tagColor: AMBER,
  },
  {
    fn: "json.dumps(obj)",
    sig: "dict → string",
    use: "Logging or sending via HTTP",
    code: 'import json\n\ndata = {"name": "John"}\ntext = json.dumps(data, indent=2)\n\nprint(text)        # formatted JSON string\nprint(type(text))  # <class \'str\'>',
    tag: "STRING",
    tagColor: PURPLE,
  },
];

const QUIZ_QUESTIONS = [
  {
    q: "What Python type does a JSON object {} map to?",
    options: ["list", "tuple", "dict", "set"],
    answer: 2,
    explain: "JSON objects (key-value pairs inside {}) always become Python dicts.",
  },
  {
    q: "Which function reads JSON from a file object?",
    options: ["json.loads()", "json.load()", "json.read()", "json.parse()"],
    answer: 1,
    explain: "json.load(f) reads from a file object. json.loads(s) reads from a string — the 's' stands for string.",
  },
  {
    q: "How do you safely get a key that might not exist?",
    options: ['data["key"]', "data.fetch('key')", "data.get('key', default)", "json.get(data, 'key')"],
    answer: 2,
    explain: "dict.get('key', default) returns default instead of raising a KeyError if the key is missing.",
  },
  {
    q: "What does json.dumps() return?",
    options: ["A file", "A dict", "A list", "A string"],
    answer: 3,
    explain: "json.dumps() converts a Python object to a JSON-formatted string. Use json.dump() (no 's') to write to a file.",
  },
  {
    q: "What is data['address'][1]['city'] on the sample JSON?",
    options: ["New Delhi-0", "Delhi", "New Delhi-1", "English"],
    answer: 2,
    explain: "data['address'] is a list of 2 dicts. Index [1] is the second item. Its 'city' key is 'New Delhi-1'.",
  },
];

const TOKEN_RE = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\b(?:import|from|with|as|for|in|return|def|class|if|else|elif|try|except|raise|True|False|None|and|or|not)\b|\b\d+\.?\d*\b)/g;

function CodeBlock({ code, highlight }) {
  const colorize = (line) => {
    if (line.trim().startsWith("#")) return <span style={{ color: GREEN }}>{line}</span>;
    return line.split(TOKEN_RE).map((part, i) => {
      if (/^["']/.test(part)) return <span key={i} style={{ color: STRING }}>{part}</span>;
      if (/^(?:import|from|with|as|for|in|return|def|class|if|else|elif|try|except|raise|and|or|not)$/.test(part))
        return <span key={i} style={{ color: PURPLE }}>{part}</span>;
      if (/^(?:True|False|None)$/.test(part)) return <span key={i} style={{ color: TEAL }}>{part}</span>;
      if (/^\d/.test(part)) return <span key={i} style={{ color: NUM }}>{part}</span>;
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <pre
      style={{
        background: "#0A1520",
        border: `1px solid ${highlight ? AMBER : "#1E3050"}`,
        borderRadius: 8,
        padding: "14px 16px",
        margin: 0,
        fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
        fontSize: 13,
        lineHeight: 1.7,
        color: TEXT,
        overflowX: "auto",
        transition: "border-color 0.2s",
      }}
    >
      {code.split("\n").map((line, i) => (
        <div key={i}>{colorize(line)}</div>
      ))}
    </pre>
  );
}

function JsonToken({ value, path, onHover, hovered }) {
  const isHovered = hovered === path;
  if (typeof value === "string")
    return (
      <span
        onMouseEnter={() => onHover(path)}
        onMouseLeave={() => onHover(null)}
        style={{
          color: STRING,
          cursor: "pointer",
          background: isHovered ? "#2A1A10" : "transparent",
          borderRadius: 3,
          padding: "0 2px",
          transition: "background 0.15s",
        }}
      >
        "{value}"
      </span>
    );
  if (typeof value === "number") return <span style={{ color: NUM }}>{value}</span>;
  if (typeof value === "boolean") return <span style={{ color: TEAL }}>{String(value)}</span>;
  if (value === null) return <span style={{ color: MUTED }}>null</span>;
  return null;
}

function JsonTree({ obj, path = "data", depth = 0, onHover, hovered }) {
  const indent = depth * 16;

  if (Array.isArray(obj)) {
    return (
      <span>
        {"[\n"}
        {obj.map((item, i) => (
          <span key={i}>
            <span style={{ paddingLeft: indent + 16 }}>
              {typeof item === "object" && item !== null ? (
                <JsonTree obj={item} path={`${path}[${i}]`} depth={depth + 1} onHover={onHover} hovered={hovered} />
              ) : (
                <JsonToken value={item} path={`${path}[${i}]`} onHover={onHover} hovered={hovered} />
              )}
              {i < obj.length - 1 ? "," : ""}
            </span>
            {"\n"}
          </span>
        ))}
        <span style={{ paddingLeft: indent }}>]</span>
      </span>
    );
  }

  if (typeof obj === "object" && obj !== null) {
    const keys = Object.keys(obj);
    return (
      <span>
        {"{\n"}
        {keys.map((key, i) => {
          const fullPath = depth === 0 ? `data["${key}"]` : `${path}["${key}"]`;
          const isHovered = hovered === fullPath;
          return (
            <span key={key}>
              <span style={{ paddingLeft: indent + 16 }}>
                <span
                  onMouseEnter={() => onHover(fullPath)}
                  onMouseLeave={() => onHover(null)}
                  style={{
                    color: AMBER,
                    cursor: "pointer",
                    fontWeight: 500,
                    background: isHovered ? "#2A1E08" : "transparent",
                    borderRadius: 3,
                    padding: "0 2px",
                    transition: "background 0.15s",
                  }}
                >
                  "{key}"
                </span>
                <span style={{ color: MUTED }}>: </span>
                {typeof obj[key] === "object" && obj[key] !== null ? (
                  <JsonTree obj={obj[key]} path={fullPath} depth={depth + 1} onHover={onHover} hovered={hovered} />
                ) : (
                  <JsonToken value={obj[key]} path={fullPath} onHover={onHover} hovered={hovered} />
                )}
                {i < keys.length - 1 ? "," : ""}
              </span>
              {"\n"}
            </span>
          );
        })}
        <span style={{ paddingLeft: indent }}>{"}"}</span>
      </span>
    );
  }

  return null;
}

function ConceptCard({ c, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "12px 16px",
        borderRadius: 8,
        cursor: "pointer",
        border: `1px solid ${active ? AMBER : "#1E3050"}`,
        background: active ? "#1C1A08" : NAVY2,
        transition: "all 0.18s",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: active ? AMBER : TEXT2 }}>{c.title}</div>
    </div>
  );
}

const CLASS_CODE = `import json
import os

class JsonReader:
    """Reads a JSON file and provides clean accessors."""

    REQUIRED_KEYS = ["name", "course", "address"]

    def __init__(self, filepath: str):
        self.filepath = filepath
        self.data = self._load()      # auto-load on creation
        self._validate()

    def _load(self) -> dict:
        """Private method — opens and parses the JSON file."""
        if not os.path.exists(self.filepath):
            raise FileNotFoundError(f"File not found: {self.filepath}")
        try:
            with open(self.filepath, "r", encoding="utf-8") as f:
                return json.load(f)
        except json.JSONDecodeError as e:
            raise ValueError(f"Invalid JSON: {e}")

    def _validate(self):
        """Check all required keys are present."""
        missing = [k for k in self.REQUIRED_KEYS if k not in self.data]
        if missing:
            raise KeyError(f"Missing required keys: {missing}")

    def get(self, key: str, default=None):
        """Safely fetch any top-level key."""
        return self.data.get(key, default)

    def get_addresses(self) -> list:
        """Return the address list (empty list if absent)."""
        return self.data.get("address", [])

    def summary(self):
        """Print a human-readable summary."""
        print(f"Name   : {self.get('name')}")
        print(f"Course : {self.get('course')}")
        for i, addr in enumerate(self.get_addresses()):
            print(f"Address[{i}]: {addr['city']}, {addr['state']}")


# ── Usage ────────────────────────────────────────────────────
try:
    reader = JsonReader("cluster.json")
    reader.summary()

    name    = reader.get("name")
    cities  = [a["city"] for a in reader.get_addresses()]
    print(cities)   # ['New Delhi-0', 'New Delhi-1']

except FileNotFoundError as e:
    print(f"[ERROR] {e}")
except ValueError as e:
    print(f"[ERROR] Bad JSON — {e}")
except KeyError as e:
    print(f"[ERROR] Schema mismatch — {e}")`;

const NAV = [
  { id: "concepts", label: "Concepts" },
  { id: "explorer", label: "JSON Explorer" },
  { id: "methods", label: "json module" },
  { id: "class", label: "JsonReader class" },
  { id: "quiz", label: "Quiz" },
];

export default function PythonJson() {
  const [section, setSection] = useState("concepts");
  const [activeConcept, setActiveConcept] = useState(0);
  const [activeMethod, setActiveMethod] = useState(0);
  const [hoveredPath, setHoveredPath] = useState(null);
  const [quizIdx, setQuizIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const handleAnswer = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === QUIZ_QUESTIONS[quizIdx].answer) setScore((s) => s + 1);
  };

  const nextQ = () => {
    if (quizIdx + 1 >= QUIZ_QUESTIONS.length) {
      setDone(true);
      return;
    }
    setQuizIdx((q) => q + 1);
    setSelected(null);
  };

  const restartQuiz = () => {
    setQuizIdx(0);
    setSelected(null);
    setScore(0);
    setDone(false);
  };

  return (
    <div style={{ background: NAVY, minHeight: "100vh", fontFamily: "'Inter', 'Segoe UI', sans-serif", color: TEXT }}>
      {/* Header */}
      <div style={{ borderBottom: "1px solid #1E3050", padding: "20px 32px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ background: AMBER, width: 8, height: 8, borderRadius: "50%" }} />
          <span style={{ fontSize: 11, fontFamily: "monospace", color: MUTED, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Python Learning Series
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: TEXT, letterSpacing: "-0.02em" }}>
            JSON in Python
          </h1>
          <span style={{ fontFamily: "monospace", fontSize: 13, color: AMBER }}>import json</span>
        </div>
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #1E3050", marginBottom: -1 }}>
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setSection(n.id)}
              style={{
                padding: "8px 18px",
                fontSize: 13,
                background: "transparent",
                border: "none",
                borderBottom: section === n.id ? `2px solid ${AMBER}` : "2px solid transparent",
                color: section === n.id ? AMBER : MUTED,
                cursor: "pointer",
                fontWeight: section === n.id ? 600 : 400,
                transition: "all 0.15s",
              }}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "28px 32px", maxWidth: 900, margin: "0 auto" }}>
        {/* CONCEPTS */}
        {section === "concepts" && (
          <div>
            <p style={{ color: TEXT2, fontSize: 14, marginTop: 0, marginBottom: 20 }}>
              JSON maps directly to Python's built-in types. Click a concept to see both representations side by side.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 20 }}>
              {CONCEPTS.map((c, i) => (
                <ConceptCard key={i} c={c} active={activeConcept === i} onClick={() => setActiveConcept(i)} />
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
                  JSON
                </div>
                <CodeBlock code={CONCEPTS[activeConcept].json} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
                  Python
                </div>
                <CodeBlock code={CONCEPTS[activeConcept].python} />
              </div>
            </div>
            <div style={{ marginTop: 14, background: NAVY2, border: "1px solid #1E3050", borderLeft: `3px solid ${AMBER}`, borderRadius: 8, padding: "12px 16px", fontSize: 13, color: TEXT2, lineHeight: 1.6 }}>
              {CONCEPTS[activeConcept].note}
            </div>
          </div>
        )}

        {/* EXPLORER */}
        {section === "explorer" && (
          <div>
            <p style={{ color: TEXT2, fontSize: 14, marginTop: 0, marginBottom: 20 }}>
              Hover over any key in the JSON tree to see the exact Python expression to access it.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
                  cluster.json — hover a key
                </div>
                <div style={{ background: "#0A1520", border: "1px solid #1E3050", borderRadius: 8, padding: "14px 16px" }}>
                  <pre style={{ margin: 0, fontFamily: "monospace", fontSize: 13, lineHeight: 1.8, color: TEXT, whiteSpace: "pre-wrap" }}>
                    <JsonTree obj={sampleJson} onHover={setHoveredPath} hovered={hoveredPath} />
                  </pre>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
                  Python access expression
                </div>
                <div style={{ background: "#0A1520", border: `1px solid ${hoveredPath ? AMBER : "#1E3050"}`, borderRadius: 8, padding: "14px 16px", minHeight: 180, display: "flex", flexDirection: "column", justifyContent: "center", transition: "border-color 0.2s" }}>
                  {hoveredPath ? (
                    <>
                      <div style={{ fontFamily: "monospace", fontSize: 18, color: AMBER, fontWeight: 700, marginBottom: 12 }}>
                        {hoveredPath}
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: 12, color: TEXT2, lineHeight: 1.6 }}>
                        <span style={{ color: GREEN }}># Full access example</span>
                        {"\n"}
                        <span style={{ color: PURPLE }}>import</span> json{"\n\n"}
                        <span style={{ color: PURPLE }}>with</span> open(<span style={{ color: STRING }}>"cluster.json"</span>) <span style={{ color: PURPLE }}>as</span> f:{"\n"}
                        {"    "}data = json.load(f){"\n\n"}
                        value = <span style={{ color: AMBER }}>{hoveredPath}</span>
                        {"\n"}
                        print(value)
                      </div>
                    </>
                  ) : (
                    <div style={{ color: MUTED, fontSize: 13, textAlign: "center" }}>← hover a key to see Python access</div>
                  )}
                </div>
                <div style={{ marginTop: 14, background: NAVY2, border: "1px solid #1E3050", borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, letterSpacing: "0.1em", marginBottom: 8, textTransform: "uppercase" }}>Quick reference</div>
                  {[
                    ['data["name"]', "→  str"],
                    ['data["course"]', "→  str"],
                    ['data["address"]', "→  list (2 items)"],
                    ['data["address"][0]', "→  dict"],
                    ['data["address"][0]["city"]', "→  'New Delhi-0'"],
                    ['data["address"][1]["city"]', "→  'New Delhi-1'"],
                  ].map(([expr, val]) => (
                    <div key={expr} style={{ display: "flex", justifyContent: "space-between", fontFamily: "monospace", fontSize: 12, padding: "3px 0", borderBottom: "1px solid #1E3050", color: TEXT2 }}>
                      <span style={{ color: AMBER }}>{expr}</span>
                      <span style={{ color: MUTED }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* METHODS */}
        {section === "methods" && (
          <div>
            <p style={{ color: TEXT2, fontSize: 14, marginTop: 0, marginBottom: 6 }}>
              The <code style={{ fontFamily: "monospace", color: AMBER, fontSize: 13 }}>json</code> module has four functions. The rule: no "s" → file, "s" suffix → string.
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
              {METHODS.map((m, i) => (
                <button
                  key={i}
                  onClick={() => setActiveMethod(i)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: `1px solid ${activeMethod === i ? m.tagColor : "#1E3050"}`,
                    background: activeMethod === i ? "#0A1520" : "transparent",
                    color: activeMethod === i ? m.tagColor : MUTED,
                    fontFamily: "monospace",
                    fontSize: 13,
                    cursor: "pointer",
                    fontWeight: 600,
                    transition: "all 0.15s",
                  }}
                >
                  {m.fn}
                </button>
              ))}
            </div>
            {(() => {
              const m = METHODS[activeMethod];
              return (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 700, color: m.tagColor }}>{m.fn}</div>
                    <span style={{ background: m.tagColor + "22", color: m.tagColor, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, letterSpacing: "0.08em" }}>{m.tag}</span>
                    <span style={{ color: MUTED, fontSize: 13 }}>— {m.use}</span>
                  </div>
                  <div style={{ color: TEXT2, fontSize: 13, marginBottom: 10, fontFamily: "monospace" }}>
                    <span style={{ color: MUTED }}>signature: </span>{m.sig}
                  </div>
                  <CodeBlock code={m.code} highlight />
                </div>
              );
            })()}
            <div style={{ marginTop: 20, background: NAVY2, border: "1px solid #1E3050", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Memory trick</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  ["load / dump", "no 's' → works with files", TEAL],
                  ["loads / dumps", "'s' = string → works with strings", PURPLE],
                ].map(([fn, desc, color]) => (
                  <div key={fn} style={{ background: "#0A1520", borderRadius: 6, padding: "10px 14px", border: `1px solid ${color}33` }}>
                    <div style={{ fontFamily: "monospace", fontSize: 14, color, fontWeight: 700, marginBottom: 4 }}>{fn}</div>
                    <div style={{ fontSize: 12, color: TEXT2 }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CLASS */}
        {section === "class" && (
          <div>
            <p style={{ color: TEXT2, fontSize: 14, marginTop: 0, marginBottom: 20 }}>
              Wrapping json.load() in a class gives you a reusable, readable interface. Here's the full JsonReader with error handling.
            </p>
            <CodeBlock code={CLASS_CODE} highlight />
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ["__init__", "Called when you write JsonReader('file.json'). Triggers _load and _validate automatically.", AMBER],
                ["_load()", "Private method (leading underscore = convention for internal use). Raises clear errors instead of raw exceptions.", TEAL],
                ["_validate()", "List comprehension checks for missing keys. Fails early with a descriptive error.", PURPLE],
                ["get_addresses()", "Returns [] by default — safe to iterate even if 'address' key is absent from JSON.", STRING],
              ].map(([name, desc, color]) => (
                <div key={name} style={{ background: NAVY2, border: "1px solid #1E3050", borderLeft: `3px solid ${color}`, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontFamily: "monospace", fontSize: 13, color, fontWeight: 700, marginBottom: 4 }}>{name}</div>
                  <div style={{ fontSize: 12, color: TEXT2, lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* QUIZ */}
        {section === "quiz" && (
          <div>
            {!done ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <p style={{ color: TEXT2, fontSize: 14, margin: 0 }}>Test your understanding — {QUIZ_QUESTIONS.length} questions.</p>
                  <span style={{ fontFamily: "monospace", fontSize: 13, color: MUTED }}>
                    {quizIdx + 1} / {QUIZ_QUESTIONS.length} • score: <span style={{ color: AMBER }}>{score}</span>
                  </span>
                </div>
                <div style={{ background: NAVY2, border: "1px solid #1E3050", borderRadius: 10, padding: "20px 24px" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 20, lineHeight: 1.5 }}>
                    {QUIZ_QUESTIONS[quizIdx].q}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {QUIZ_QUESTIONS[quizIdx].options.map((opt, i) => {
                      const isCorrect = i === QUIZ_QUESTIONS[quizIdx].answer;
                      const isSelected = selected === i;
                      let bg = NAVY3, border = "#1E3050", color = TEXT2;
                      if (selected !== null) {
                        if (isCorrect) { bg = "#0A2010"; border = "#2A6030"; color = "#6FCF97"; }
                        else if (isSelected) { bg = "#2A0A0A"; border = "#6B2020"; color = "#EB5757"; }
                      }
                      return (
                        <button
                          key={i}
                          onClick={() => handleAnswer(i)}
                          style={{
                            textAlign: "left",
                            padding: "10px 16px",
                            borderRadius: 6,
                            border: `1px solid ${border}`,
                            background: bg,
                            color,
                            fontFamily: "monospace",
                            fontSize: 13,
                            cursor: selected === null ? "pointer" : "default",
                            transition: "all 0.15s",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {selected !== null && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.6, background: "#0A1520", borderRadius: 6, padding: "10px 14px", marginBottom: 12 }}>
                        {QUIZ_QUESTIONS[quizIdx].explain}
                      </div>
                      <button
                        onClick={nextQ}
                        style={{
                          padding: "8px 20px",
                          background: AMBER,
                          color: NAVY,
                          border: "none",
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        {quizIdx + 1 < QUIZ_QUESTIONS.length ? "Next →" : "See result →"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 48, fontWeight: 800, color: AMBER, fontFamily: "monospace" }}>{score}/{QUIZ_QUESTIONS.length}</div>
                <div style={{ fontSize: 16, color: TEXT2, margin: "12px 0 24px" }}>
                  {score === QUIZ_QUESTIONS.length ? "Perfect score! You've got JSON handling down." : score >= 3 ? "Good understanding — review the missed ones above." : "Keep going — revisit the Concepts and json module tabs."}
                </div>
                <button
                  onClick={restartQuiz}
                  style={{
                    padding: "10px 24px",
                    background: "transparent",
                    color: AMBER,
                    border: `1px solid ${AMBER}`,
                    borderRadius: 6,
                    fontSize: 14,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Restart quiz
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
